import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, ChevronUp, Clock, Check, Plus, Minus } from 'lucide-react';

// ProductScreen (tela de detalhes do produto do cardapio publico).
// Extraido VERBATIM de PublicMenu.js (Fase 2 refactor). Renderizado como
// <ProductScreen .../> (mesmo padrao dos demais screens JSX ja extraidos),
// recebendo estado/handlers de closure via props. Regra de ouro: zero mudanca.
const ProductScreen = ({ selectedProduct, additionalGroups, expandedImage, setExpandedImage, setCurrentScreen, addToCart }) => {
    const [selectedAdditionals, setSelectedAdditionals] = useState({});
    const [groupSelections, setGroupSelections] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationMessage, setValidationMessage] = useState({ title: '', message: '' });
    const [productNotes, setProductNotes] = useState('');
    const [showMaxLimitModal, setShowMaxLimitModal] = useState(false);
    const [maxLimitMessage, setMaxLimitMessage] = useState({ groupName: '', maxChoices: 0 });
    
    // Refs e estados para auto-scroll de grupos obrigatórios
    const groupRefs = useRef({});
    const [nextRequiredGroupId, setNextRequiredGroupId] = useState(null);

    // Get product's additional groups
    const productGroups = React.useMemo(() => {
      if (!selectedProduct || !selectedProduct.additional_groups || selectedProduct.additional_groups.length === 0) {
        return [];
      }
      
      return selectedProduct.additional_groups
        .map(groupId => additionalGroups.find(g => g.id === groupId))
        .filter(Boolean);
    }, [selectedProduct, additionalGroups]);

    // Helper to create unique key for additional in a specific group
    const getAdditionalKey = (groupId, additionalId) => `${groupId}_${additionalId}`;

    const updateAdditionalInGroup = (groupId, additional, isSelected) => {
      const group = productGroups.find(g => g.id === groupId);
      if (!group) return;

      const currentGroupSelections = groupSelections[groupId] || [];
      const additionalKey = getAdditionalKey(groupId, additional.id);
      
      let newSelections;
      let newAdditionals = { ...selectedAdditionals };
      
      // If max_choices = 1 (radio behavior), replace selection
      if (group.max_choices === 1) {
        if (isSelected) {
          // Clear previous selection in this group (using composite keys)
          currentGroupSelections.forEach(prevId => {
            const prevKey = getAdditionalKey(groupId, prevId);
            delete newAdditionals[prevKey];
          });
          
          newSelections = [additional.id];
          newAdditionals[additionalKey] = { ...additional, quantity: 1, groupId };
        } else {
          newSelections = [];
          delete newAdditionals[additionalKey];
        }
      } else {
        // Multiple choice behavior
        if (isSelected) {
          // Check if we can add more
          if (currentGroupSelections.length >= group.max_choices) {
            alert(`Você pode escolher no máximo ${group.max_choices} ${group.max_choices === 1 ? 'item' : 'itens'} neste grupo`);
            return;
          }
          newSelections = [...currentGroupSelections, additional.id];
          newAdditionals[additionalKey] = { ...additional, quantity: 1, groupId };
        } else {
          newSelections = currentGroupSelections.filter(id => id !== additional.id);
          delete newAdditionals[additionalKey];
        }
      }

      setGroupSelections({
        ...groupSelections,
        [groupId]: newSelections
      });

      setSelectedAdditionals(newAdditionals);

      // Clear validation error for this group
      if (validationErrors[groupId]) {
        const newErrors = { ...validationErrors };
        delete newErrors[groupId];
        setValidationErrors(newErrors);
      }
    };

    // Conta o TOTAL de seleções de um grupo pela SOMA DAS QUANTIDADES
    // (mesma métrica usada na validação de MÁXIMO ao incrementar). Assim,
    // repetir o mesmo adicional (ex.: 2x Cheddar num grupo min2/max2) conta
    // como 2 e satisfaz o mínimo — antes o mínimo contava apenas itens
    // DISTINTOS, o que travava o cliente (dizia "escolha pelo menos 2" mesmo
    // com 2 unidades já selecionadas).
    const getGroupQuantity = (groupId) => Object.values(selectedAdditionals)
      .filter(add => add.groupId === groupId)
      .reduce((sum, add) => sum + (add.quantity || 0), 0);
    const validateSelections = () => {
      const errors = {};
      
      productGroups.forEach(group => {
        const count = getGroupQuantity(group.id);
        
        if (group.is_required && count < group.min_choices) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        } else if (count < group.min_choices && count > 0) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        }
      });
      
      setValidationErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const getTotalPrice = () => {
      const additionalTotal = Object.values(selectedAdditionals).reduce((sum, add) => sum + add.price * add.quantity, 0);
      return selectedProduct.price + additionalTotal;
    };

    // Função para encontrar o próximo grupo obrigatório não preenchido
    const findNextRequiredGroup = () => {
      for (const group of productGroups) {
        if (group.is_required) {
          const minRequired = group.min_choices || 1;
          if (getGroupQuantity(group.id) < minRequired) {
            return group.id;
          }
        }
      }
      return null;
    };

    // Função para fazer scroll suave até o próximo grupo obrigatório
    const scrollToNextRequiredGroup = () => {
      const nextGroupId = findNextRequiredGroup();
      if (nextGroupId && groupRefs.current[nextGroupId]) {
        setTimeout(() => {
          groupRefs.current[nextGroupId].scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          groupRefs.current[nextGroupId].style.transition = 'all 0.3s ease';
          groupRefs.current[nextGroupId].style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.5)';
          setTimeout(() => {
            if (groupRefs.current[nextGroupId]) {
              groupRefs.current[nextGroupId].style.boxShadow = '';
            }
          }, 1500);
        }, 150);
      }
      setNextRequiredGroupId(nextGroupId);
    };

    // Handler para fechar o modal de validação e fazer scroll
    const handleCloseValidationModal = () => {
      setShowValidationModal(false);
      scrollToNextRequiredGroup();
    };

    const handleAddToCart = () => {
      // Criar validação inline para garantir que funciona
      const errors = {};
      
      productGroups.forEach(group => {
        const count = getGroupQuantity(group.id);
        
        // Se o grupo é obrigatório e nenhum item foi selecionado
        if (group.is_required && count === 0) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices || 1} ${(group.min_choices || 1) === 1 ? 'item' : 'itens'}`;
        } 
        // Se o grupo tem min_choices e não foi atingido (conta por SOMA DE QUANTIDADES)
        else if (group.min_choices && count > 0 && count < group.min_choices) {
          errors[group.id] = `Escolha pelo menos ${group.min_choices} ${group.min_choices === 1 ? 'item' : 'itens'}`;
        }
      });
      
      // Atualizar os erros de validação no estado
      setValidationErrors(errors);
      
      // Se houver erros, mostrar modal e não prosseguir
      if (Object.keys(errors).length > 0) {
        const firstErrorGroup = productGroups.find(g => errors[g.id]);
        
        if (firstErrorGroup) {
          setValidationMessage({
            title: 'Campo Obrigatório',
            message: `O grupo "${firstErrorGroup.name}" é obrigatório.\n\n${errors[firstErrorGroup.id]}`
          });
        } else {
          setValidationMessage({
            title: 'Atenção',
            message: 'Existem campos obrigatórios que precisam ser preenchidos.'
          });
        }
        setShowValidationModal(true);
        return;
      }
      
      // Se passou na validação, adiciona ao carrinho
      addToCart(selectedProduct, selectedAdditionals, productNotes);
    };

    return (
      <div className="fixed inset-0 bg-white overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Imagem do Produto - Expansível in-place */}
        <div 
          className={`relative cursor-pointer overflow-hidden ${
            expandedImage ? 'h-96' : 'h-64'
          }`}
          style={{
            transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
          onClick={() => setExpandedImage(!expandedImage)}
        >
          <img 
            src={selectedProduct.image_url || "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg"} 
            alt={selectedProduct.name}
            className="w-full h-full object-cover"
            loading="eager"
            onError={(e) => {
              e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
            }}
          />
          {/* Indicador de expandir/retrair com animação suave */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
            <span className="text-white/90 text-xs bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-sm flex items-center gap-1.5 shadow-lg">
              <ChevronUp 
                size={16} 
                style={{
                  transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  transform: expandedImage ? 'rotate(180deg)' : 'rotate(0deg)',
                  animation: expandedImage ? 'none' : 'soft-bounce 2s ease-in-out infinite'
                }}
              />
              {expandedImage ? 'Retrair' : 'Ampliar'}
            </span>
          </div>
          {/* Navegação Superior sobre a imagem */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/50 to-transparent">
            <button 
              onClick={(e) => { e.stopPropagation(); setCurrentScreen('home'); }}
              className="bg-black/30 backdrop-blur-sm text-white p-2 rounded-full hover:bg-black/50 transition-colors"
            >
              <ArrowLeft size={22} />
            </button>
          </div>
        </div>

        {/* Header - Informações do Produto */}
        <div className="bg-white px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-2">
            {selectedProduct.name}
          </h1>
          <div className="flex items-center gap-3 mb-2">
            <p className="text-lg font-bold text-gray-900">
              {selectedProduct.price > 0 ? `R$ ${selectedProduct.price.toFixed(2)}` : 'Preço conforme opções'}
            </p>
            {selectedProduct.preparation_time && (
              <span className="text-sm text-gray-400 flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full">
                <Clock size={14} />
                {selectedProduct.preparation_time} min
              </span>
            )}
          </div>
          {selectedProduct.description && (
            <p className="text-gray-600 text-sm leading-relaxed">
              {selectedProduct.description}
            </p>
          )}
        </div>

        {/* Área de Conteúdo - Fundo Claro */}
        <div className="bg-gray-50 px-4 pb-32 pt-2">
          {/* Grupos de Adicionais - Estilo Minimalista */}
          {productGroups.length > 0 && (
            <div className="space-y-1">
              {productGroups.map((group) => {
                const isSingleChoice = group.max_choices === 1;
                
                return (
                  <div key={group.id} ref={el => groupRefs.current[group.id] = el} data-group-id={group.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {/* Group Header - Fundo Destacado com Melhor Hierarquia Visual */}
                    <div className="px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 flex items-start gap-3">
                          {/* Ícone de Grupo */}
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center shadow-sm">
                              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                          </div>
                          
                          {/* Título e Descrição do Grupo */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-extrabold text-gray-900 tracking-tight uppercase mb-1">
                              {group.name}
                            </h3>
                            <p className="text-xs font-medium text-blue-700 flex items-center gap-1.5">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              {group.max_choices === 1 ? 'Escolha até 1 item' : `Escolha até ${group.max_choices} itens`}
                            </p>
                          </div>
                        </div>
                        
                        {/* Badge de Obrigatório */}
                        {(() => {
                          const hasSelection = (groupSelections[group.id] || []).length > 0;
                          
                          if (group.is_required) {
                            return (
                              <div className="flex items-center flex-shrink-0 ml-2">
                                <span className={`px-3 py-1.5 text-xs font-bold rounded-full shadow-sm transition-all ${
                                  hasSelection 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-red-500 text-white animate-pulse'
                                }`}>
                                  {hasSelection ? '✓ Completo' : '⚠ Obrigatório'}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {validationErrors[group.id] && (
                      <div className="px-4 py-3 bg-red-50 border-b border-red-100">
                        <p className="text-sm text-red-700 font-medium">
                          ⚠️ {validationErrors[group.id]}
                        </p>
                      </div>
                    )}

                    {/* Itens do Grupo - ESCOLHA ÚNICA (Radio) */}
                    {isSingleChoice ? (
                      <div className="divide-y divide-gray-100">
                        {group.additionals && group.additionals.map((additional) => {
                          const isSelected = (groupSelections[group.id] || []).includes(additional.id);
                          
                          return (
                            <button
                              key={additional.id}
                              onClick={() => updateAdditionalInGroup(group.id, additional, !isSelected)}
                              className={`w-full px-4 py-4 text-left transition-all ${
                                isSelected ? 'bg-primary-light' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-base leading-snug">
                                    {additional.name}
                                  </p>
                                  {additional.description && (
                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                      {additional.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {additional.price > 0 && (
                                    <p className="text-base font-bold text-gray-900">
                                      R$ {additional.price.toFixed(2)}
                                    </p>
                                  )}
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                    isSelected 
                                      ? 'border-primary bg-primary scale-110' 
                                      : 'border-gray-300 bg-white'
                                  }`}>
                                    {isSelected && (
                                      <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      /* Itens do Grupo - MÚLTIPLA ESCOLHA (Contador ou Checkbox se limit_one_per_item) */
                      <div className="divide-y divide-gray-100">
                        {group.additionals && group.additionals.map((additional) => {
                          const additionalKey = getAdditionalKey(group.id, additional.id);
                          const quantity = selectedAdditionals[additionalKey]?.quantity || 0;
                          const totalSelected = Object.values(selectedAdditionals)
                            .filter(add => add.groupId === group.id)
                            .reduce((sum, add) => sum + add.quantity, 0);
                          const isMaxReached = totalSelected >= group.max_choices && quantity === 0;
                          const limitOnePerItem = group.limit_one_per_item || false;
                          
                          return (
                            <div 
                              key={additional.id} 
                              className={`px-4 py-4 transition-colors ${
                                quantity > 0 ? 'bg-primary-light' : ''
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 text-base leading-snug">
                                    {additional.name}
                                  </p>
                                  {additional.description && (
                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                                      {additional.description}
                                    </p>
                                  )}
                                  {additional.price > 0 && (
                                    <p className="text-base font-bold text-gray-900 mt-1.5">
                                      R$ {additional.price.toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                
                                {/* Controles de Quantidade - Com suporte a limit_one_per_item */}
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  {limitOnePerItem ? (
                                    /* MODO CHECKBOX - limit_one_per_item = true */
                                    quantity > 0 ? (
                                      <button
                                        onClick={() => {
                                          const newAdditionals = { ...selectedAdditionals };
                                          delete newAdditionals[additionalKey];
                                          setSelectedAdditionals(newAdditionals);
                                          
                                          const newSelections = (groupSelections[group.id] || [])
                                            .filter(id => id !== additional.id);
                                          setGroupSelections({
                                            ...groupSelections,
                                            [group.id]: newSelections
                                          });
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-green-500 hover:bg-green-600 transition-colors rounded-full shadow-md"
                                      >
                                        <Check size={18} className="text-white" strokeWidth={3} />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          if (!isMaxReached) {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: 1, groupId: group.id }
                                            });
                                            
                                            const newSelections = [...(groupSelections[group.id] || []), additional.id];
                                            setGroupSelections({
                                              ...groupSelections,
                                              [group.id]: newSelections
                                            });
                                          }
                                        }}
                                        disabled={isMaxReached}
                                        className={`w-8 h-8 flex items-center justify-center transition-colors rounded-full shadow-sm ${
                                          isMaxReached 
                                            ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed' 
                                            : 'bg-primary hover:bg-primary-hover'
                                        }`}
                                      >
                                        <Plus size={18} className={isMaxReached ? 'text-gray-400' : 'text-white'} />
                                      </button>
                                    )
                                  ) : (
                                    /* MODO CONTADOR - limit_one_per_item = false (comportamento padrão) */
                                    quantity > 0 ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          const newQty = quantity - 1;
                                          if (newQty === 0) {
                                            const newAdditionals = { ...selectedAdditionals };
                                            delete newAdditionals[additionalKey];
                                            setSelectedAdditionals(newAdditionals);
                                            
                                            const newSelections = (groupSelections[group.id] || [])
                                              .filter(id => id !== additional.id);
                                            setGroupSelections({
                                              ...groupSelections,
                                              [group.id]: newSelections
                                            });
                                          } else {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: newQty, groupId: group.id }
                                            });
                                          }
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 hover:border-gray-400 transition-colors rounded-full"
                                      >
                                        <Minus size={16} className="text-gray-700" />
                                      </button>
                                      
                                      <span className="w-8 text-center font-bold text-gray-900 text-lg">
                                        {quantity}
                                      </span>
                                      
                                      <button
                                        onClick={() => {
                                          const newQty = quantity + 1;
                                          // Calcular total de itens já selecionados no grupo (incluindo o incremento)
                                          const totalAfterIncrement = totalSelected + 1;
                                          
                                          if (totalAfterIncrement <= group.max_choices) {
                                            setSelectedAdditionals({
                                              ...selectedAdditionals,
                                              [additionalKey]: { ...additional, quantity: newQty, groupId: group.id }
                                            });
                                          } else {
                                            // Mostrar modal amigável em vez de alert
                                            setMaxLimitMessage({
                                              groupName: group.name,
                                              maxChoices: group.max_choices
                                            });
                                            setShowMaxLimitModal(true);
                                          }
                                        }}
                                        className="w-8 h-8 flex items-center justify-center bg-primary hover:bg-primary-hover transition-colors rounded-full shadow-md"
                                      >
                                        <Plus size={16} className="text-white" />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        if (!isMaxReached) {
                                          setSelectedAdditionals({
                                            ...selectedAdditionals,
                                            [additionalKey]: { ...additional, quantity: 1, groupId: group.id }
                                          });
                                          
                                          const newSelections = [...(groupSelections[group.id] || []), additional.id];
                                          setGroupSelections({
                                            ...groupSelections,
                                            [group.id]: newSelections
                                          });
                                        }
                                      }}
                                      disabled={isMaxReached}
                                      className={`w-8 h-8 flex items-center justify-center transition-colors rounded-full shadow-sm ${
                                        isMaxReached 
                                          ? 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed' 
                                          : 'bg-primary hover:bg-primary-hover'
                                      }`}
                                    >
                                      <Plus size={18} className={isMaxReached ? 'text-gray-400' : 'text-white'} />
                                    </button>
                                  )
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Campo de Observações */}
          <div className={`${productGroups.length > 0 ? 'mt-1' : 'mt-2'} mb-4`}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Header - Fundo Destacado */}
              <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-300">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 className="text-base font-bold text-gray-900">Observações</h3>
                  <span className="text-xs text-gray-500 ml-auto">(Opcional)</span>
                </div>
              </div>
              
              {/* Textarea */}
              <div className="p-4">
                <textarea
                  value={productNotes}
                  onChange={(e) => setProductNotes(e.target.value)}
                  placeholder="Ex.: Tirar cebola, ovo, etc."
                  rows={3}
                  maxLength={200}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                />
                <div className="flex justify-end mt-2">
                  <span className="text-xs text-gray-500">
                    {productNotes.length}/200 caracteres
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Botão Fixo - Sempre Visível */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 shadow-lg z-30">
          <button 
            onClick={handleAddToCart}
            className="w-full bg-primary hover:bg-primary-hover text-white py-4 rounded-xl font-semibold text-base shadow-md transition-all duration-200 active:scale-95 animate-slow-pulse hover:animate-none hover:scale-105"
            data-testid="add-to-cart-button"
          >
            Avançar • R$ {getTotalPrice().toFixed(2)}
          </button>
        </div>

        {/* Modal de Validação - Design Compacto e Simpático com Portal */}
        {showValidationModal && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '16px',
              animation: 'overlayFadeIn 0.2s ease-out'
            }}
            onClick={handleCloseValidationModal}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '350px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                position: 'relative',
                zIndex: 100000,
                transformOrigin: 'center center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ícone e Título - Mais compacto */}
              <div style={{ padding: '20px 20px 12px', textAlign: 'center' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  backgroundColor: '#fed7aa', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 8px' 
                }}>
                  <svg style={{ width: '24px', height: '24px', color: '#f97316' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                  Ops! Falta algo...
                </h3>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                  {validationMessage.message}
                </p>
              </div>
              
              {/* Botão compacto */}
              <div style={{ padding: '0 20px 20px' }}>
                <button
                  onClick={handleCloseValidationModal}
                  style={{
                    width: '100%',
                    backgroundColor: '#f97316',
                    color: 'white',
                    fontWeight: '600',
                    padding: '10px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#ea580c'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#f97316'}
                >
                  Levar-me até lá! 👆
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal de Limite Máximo - Design Simpático com Portal */}
        {showMaxLimitModal && ReactDOM.createPortal(
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '16px',
              animation: 'overlayFadeIn 0.2s ease-out'
            }}
            onClick={() => setShowMaxLimitModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '350px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                animation: 'modalFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                position: 'relative',
                zIndex: 100000,
                transformOrigin: 'center center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Ícone e Título */}
              <div style={{ padding: '20px 20px 12px', textAlign: 'center' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(to bottom right, #dbeafe, #bfdbfe)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg style={{ width: '24px', height: '24px', color: '#2563eb' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', marginBottom: '8px' }}>
                  Limite Atingido! 🎯
                </h3>
                <div style={{ 
                  backgroundColor: '#eff6ff', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '8px' 
                }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e3a8a', marginBottom: '4px' }}>
                    {maxLimitMessage.groupName}
                  </p>
                  <p style={{ fontSize: '12px', color: '#1d4ed8' }}>
                    Máximo permitido: <span style={{ fontWeight: 'bold' }}>{maxLimitMessage.maxChoices} {maxLimitMessage.maxChoices === 1 ? 'item' : 'itens'}</span>
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                  Você já escolheu o máximo permitido! 😊
                  <br />
                  <span style={{ fontWeight: '500', color: '#374151' }}>Remova algum item para adicionar outro.</span>
                </p>
              </div>
              
              {/* Botão */}
              <div style={{ padding: '0 20px 20px' }}>
                <button
                  onClick={() => setShowMaxLimitModal(false)}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(to right, #3b82f6, #2563eb)',
                    color: 'white',
                    fontWeight: '600',
                    padding: '10px',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'linear-gradient(to right, #2563eb, #1d4ed8)'}
                  onMouseOut={(e) => e.target.style.background = 'linear-gradient(to right, #3b82f6, #2563eb)'}
                >
                  Entendi! ✓
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

export default ProductScreen;
